/**
 * The corpus browser's data layer — chunk 9's "search across all eight
 * drafting tables (not just law)".
 *
 * Two routers answer these calls: `server/app/routers/corpus_law.py` (law,
 * authorities — built earlier, reused here rather than duplicated) and
 * `server/app/routers/corpus_intel.py` (the other six: arguments, phrases,
 * flow, ground findings, patterns, lessons). Both share the `research:read`
 * gate `useAuth().hasScope('research:read')` checks before any of these
 * hooks are enabled.
 *
 * Every list response carries `{items, total, capped}` — this module never
 * re-derives `capped` from a client-side length check, because the server's
 * own cap is the only one that is honest about a query that matched more
 * than it returned (the no-silent-caps rule `corpus_law.py`'s module
 * docstring states).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { api } from './client'
import type {
  ArgumentsResponse,
  DecisionsResponse,
  FlowResponse,
  GroundFindingsResponse,
  LessonsResponse,
  NiceClassesResponse,
  PatternsResponse,
  PhrasesResponse,
} from './types'

/** The ten drafting tables, in the order the Corpus workspace tabs them —
 * eight from chunk 9 plus decisions and nice_classes, added later. */
export const CORPUS_TABLES = [
  'law',
  'authorities',
  'arguments',
  'phrases',
  'flow',
  'ground_findings',
  'patterns',
  'lessons',
  'decisions',
  'nice_classes',
] as const
export type CorpusTable = (typeof CORPUS_TABLES)[number]

export const CORPUS_TABLE_LABELS: Record<CorpusTable, string> = {
  law: 'Law',
  authorities: 'Authorities',
  arguments: 'Arguments',
  phrases: 'Phrases',
  flow: 'Flow',
  ground_findings: 'Ground findings',
  patterns: 'Patterns',
  lessons: 'Lessons',
  decisions: 'Decisions',
  nice_classes: 'Nice classes',
}

// ---------------------------------------------------------------------------
// Law — server/app/routers/corpus_law.py, reused rather than duplicated.
// ---------------------------------------------------------------------------

export interface OfficeCoverage {
  office: string
  chunks: number
  tagged: number
  untagged: number
  provisions: number
  grounds: number
}

export interface GroundCoverage {
  ground: string
  chunks: number
  provisions: number
}

export interface ProvisionSummary {
  office: string | null
  citation: string
  heading: string | null
  chunk_type: string | null
  chunks: number
  tagged: number
  grounds: string[]
}

export interface AuthorityEntry {
  id: number
  citation: string
  case_name: string | null
  proposition: string | null
  office: string | null
  ground: string | null
  n_citations: number | null
}
export interface AuthoritiesResponse {
  authorities: AuthorityEntry[]
  total: number
  capped: boolean
}

/** One chunk within a provision, in reading order — untagged tails included.
 * `GET /provision`'s own docstring: "the never-truncated read: what a
 * reviewer compares a draft's law block against." No pagination here, ever —
 * see `ProvisionReadingPane`'s own comment for the incident that rules it out. */
export interface ProvisionChunk {
  id: number
  ground: string | null
  page: number | null
  /** Nullable on a bridged database — see `ChunkOut` in `corpus_law.py`. */
  custom_id: string | null
  /** `null` when the row carries no stored text. Render the gap, not a blank. */
  chunk_text: string | null
}
export interface Provision {
  office: string | null
  citation: string
  heading: string | null
  chunk_type: string | null
  grounds: string[]
  chunks: ProvisionChunk[]
  text: string
  /**
   * Chunk ids in this provision that carry no stored text. `text` leaves them
   * out, so this is what stops a reviewer reading a complete-looking provision
   * that is quietly missing part of itself.
   */
  missing_text: number[]
}

export const corpusIntelKeys = {
  offices: ['corpus', 'law', 'offices'] as const,
  grounds: (office: string | null) => ['corpus', 'law', 'grounds', office ?? ''] as const,
  provisions: (office: string | null, ground: string | null) =>
    ['corpus', 'law', 'provisions', office ?? '', ground ?? ''] as const,
  authorities: (office: string | null, ground: string | null) =>
    ['corpus', 'authorities', office ?? '', ground ?? ''] as const,
  arguments: (office: string | null, ground: string | null) =>
    ['corpus', 'arguments', office ?? '', ground ?? ''] as const,
  phrases: (office: string | null, ground: string | null) =>
    ['corpus', 'phrases', office ?? '', ground ?? ''] as const,
  flow: (office: string | null) => ['corpus', 'flow', office ?? ''] as const,
  groundFindings: (office: string | null, ground: string | null) =>
    ['corpus', 'ground_findings', office ?? '', ground ?? ''] as const,
  patterns: (office: string | null, ground: string | null) =>
    ['corpus', 'patterns', office ?? '', ground ?? ''] as const,
  lessons: (office: string | null, ground: string | null) =>
    ['corpus', 'lessons', office ?? '', ground ?? ''] as const,
  provision: (office: string | null, citation: string | null) =>
    ['corpus', 'law', 'provision', office ?? '', citation ?? ''] as const,
  decisions: (office: string | null, ground: string | null, result: string | null, outcome: string | null) =>
    ['corpus', 'decisions', office ?? '', ground ?? '', result ?? '', outcome ?? ''] as const,
  niceClasses: (classNumber: number | null, q: string | null) =>
    ['corpus', 'nice_classes', classNumber ?? '', q ?? ''] as const,
}

/** Per-office law coverage — the workspace's office tab strip. */
export function useOfficeCoverage(enabled: boolean): UseQueryResult<OfficeCoverage[]> {
  return useQuery<OfficeCoverage[]>({
    queryKey: corpusIntelKeys.offices,
    queryFn: () => api.get<OfficeCoverage[]>('/api/corpus/law/offices'),
    enabled,
  })
}

/** The ground vocabulary the corpus actually tags — the ground chip row. */
export function useGroundCoverage(
  office: string | null,
  enabled: boolean,
): UseQueryResult<GroundCoverage[]> {
  return useQuery<GroundCoverage[]>({
    queryKey: corpusIntelKeys.grounds(office),
    queryFn: () =>
      api.get<GroundCoverage[]>('/api/corpus/law/grounds', { query: { office: office ?? undefined } }),
    enabled,
  })
}

export function useProvisions(
  office: string | null,
  ground: string | null,
  enabled: boolean,
): UseQueryResult<ProvisionSummary[]> {
  return useQuery<ProvisionSummary[]>({
    queryKey: corpusIntelKeys.provisions(office, ground),
    queryFn: () =>
      api.get<ProvisionSummary[]>('/api/corpus/law/provisions', {
        query: { office: office ?? undefined, ground: ground ?? undefined },
      }),
    enabled,
  })
}

export function useAuthorities(
  office: string | null,
  ground: string | null,
  enabled: boolean,
): UseQueryResult<AuthoritiesResponse> {
  return useQuery<AuthoritiesResponse>({
    queryKey: corpusIntelKeys.authorities(office, ground),
    queryFn: () =>
      api.get<AuthoritiesResponse>('/api/corpus/law/authorities', {
        query: { office: office ?? undefined, ground: ground ?? undefined },
      }),
    enabled,
  })
}

// ---------------------------------------------------------------------------
// The six chunk-9 tables — server/app/routers/corpus_intel.py.
// ---------------------------------------------------------------------------

export function useArguments(
  office: string | null,
  ground: string | null,
  enabled: boolean,
): UseQueryResult<ArgumentsResponse> {
  return useQuery<ArgumentsResponse>({
    queryKey: corpusIntelKeys.arguments(office, ground),
    queryFn: () =>
      api.get<ArgumentsResponse>('/api/corpus/arguments', {
        query: { office: office ?? undefined, ground: ground ?? undefined },
      }),
    enabled,
  })
}

export function usePhrases(
  office: string | null,
  ground: string | null,
  enabled: boolean,
): UseQueryResult<PhrasesResponse> {
  return useQuery<PhrasesResponse>({
    queryKey: corpusIntelKeys.phrases(office, ground),
    queryFn: () =>
      api.get<PhrasesResponse>('/api/corpus/phrases', {
        query: { office: office ?? undefined, ground: ground ?? undefined },
      }),
    enabled,
  })
}

export function useFlow(office: string | null, enabled: boolean): UseQueryResult<FlowResponse> {
  // `office` is optional server-side (`GET /api/corpus/flow`'s own
  // `OfficeParam = None` — omitting it aggregates across every office
  // rather than refusing), so this must not gate on `Boolean(office)` the
  // way a required-office endpoint would: doing so left the Flow tab
  // stuck on "Loading…" forever under the "all offices" filter, the tab's
  // own default, since a disabled query never resolves.
  return useQuery<FlowResponse>({
    queryKey: corpusIntelKeys.flow(office),
    queryFn: () => api.get<FlowResponse>('/api/corpus/flow', { query: { office: office ?? undefined } }),
    enabled,
  })
}

export function useGroundFindings(
  office: string | null,
  ground: string | null,
  enabled: boolean,
): UseQueryResult<GroundFindingsResponse> {
  return useQuery<GroundFindingsResponse>({
    queryKey: corpusIntelKeys.groundFindings(office, ground),
    queryFn: () =>
      api.get<GroundFindingsResponse>('/api/corpus/ground-findings', {
        query: { office: office ?? undefined, ground: ground ?? undefined },
      }),
    enabled,
  })
}

export function usePatterns(
  office: string | null,
  ground: string | null,
  enabled: boolean,
): UseQueryResult<PatternsResponse> {
  return useQuery<PatternsResponse>({
    queryKey: corpusIntelKeys.patterns(office, ground),
    queryFn: () =>
      api.get<PatternsResponse>('/api/corpus/patterns', {
        query: { office: office ?? undefined, ground: ground ?? undefined },
      }),
    enabled,
  })
}

export function useLessons(
  office: string | null,
  ground: string | null,
  enabled: boolean,
): UseQueryResult<LessonsResponse> {
  return useQuery<LessonsResponse>({
    queryKey: corpusIntelKeys.lessons(office, ground),
    queryFn: () =>
      api.get<LessonsResponse>('/api/corpus/lessons', {
        query: { office: office ?? undefined, ground: ground ?? undefined },
      }),
    enabled,
  })
}

/** One provision, whole — the in-app reading pane's own read. `citation` is
 * required server-side, so this only ever fires once one is picked. */
export function useProvision(
  office: string | null,
  citation: string | null,
  enabled: boolean,
): UseQueryResult<Provision> {
  return useQuery<Provision>({
    queryKey: corpusIntelKeys.provision(office, citation),
    queryFn: () =>
      api.get<Provision>('/api/corpus/law/provision', {
        query: { citation: citation ?? undefined, office: office ?? undefined },
      }),
    enabled: enabled && citation !== null,
  })
}

// ---------------------------------------------------------------------------
// Decisions and Nice classes — added later, alongside the six above.
// ---------------------------------------------------------------------------

export function useDecisions(
  office: string | null,
  ground: string | null,
  result: string | null,
  outcome: string | null,
  enabled: boolean,
): UseQueryResult<DecisionsResponse> {
  return useQuery<DecisionsResponse>({
    queryKey: corpusIntelKeys.decisions(office, ground, result, outcome),
    queryFn: () =>
      api.get<DecisionsResponse>('/api/corpus/decisions', {
        query: {
          office: office ?? undefined,
          ground: ground ?? undefined,
          result: result ?? undefined,
          outcome: outcome ?? undefined,
        },
      }),
    enabled,
  })
}

export function useNiceClasses(
  classNumber: number | null,
  q: string | null,
  enabled: boolean,
): UseQueryResult<NiceClassesResponse> {
  return useQuery<NiceClassesResponse>({
    queryKey: corpusIntelKeys.niceClasses(classNumber, q),
    queryFn: () =>
      api.get<NiceClassesResponse>('/api/corpus/nice-classes', {
        query: { class_number: classNumber ?? undefined, q: q ?? undefined },
      }),
    enabled,
  })
}
